// The blog index (/blog, ported from the concept site's blog.js): the category filter over
// the post cards (the URL's ?category= mirrors it), the scroll cue on the category strip,
// and the top-row marking the grid's CSS uses. The article page's hero parallax is next.

export function blog(): void {
  const filters = [...document.querySelectorAll<HTMLElement>(".category-filter")];
  if (filters.length === 0) return;
  const posts = [...document.querySelectorAll<HTMLElement>("[data-categories]")];
  const archiveGrid = document.querySelector<HTMLElement>(".post-grid");
  const featuredSection = document.querySelector<HTMLElement>(".featured");
  const featuredArchiveCard = document.querySelector<HTMLElement>("[data-featured-archive-card]");
  const categoryNav = document.querySelector<HTMLElement>(".category-nav");
  const categoryList = document.querySelector<HTMLElement>(".category-list");

  const updateCategoryCue = () => {
    if (!categoryNav || !categoryList) return;
    const atEnd =
      categoryList.scrollLeft + categoryList.clientWidth >= categoryList.scrollWidth - 4;
    categoryNav.classList.toggle("at-scroll-end", atEnd);
  };
  const markTopRow = () => {
    if (!archiveGrid) return;
    const cards = [...archiveGrid.querySelectorAll<HTMLElement>(".post-card")];
    for (const card of cards) card.classList.remove("top-row");
    const visible = cards.filter((card) => !card.hidden);
    const firstTop = visible[0]?.getBoundingClientRect().top;
    if (firstTop === undefined) return;
    for (const card of visible) {
      if (Math.abs(card.getBoundingClientRect().top - firstTop) < 2) card.classList.add("top-row");
    }
  };
  if (categoryList) {
    categoryList.addEventListener("scroll", updateCategoryCue, { passive: true });
    addEventListener("resize", updateCategoryCue, { passive: true });
    updateCategoryCue();
  }

  const applyFilter = (wanted: string, updateURL = false) => {
    const category = filters.some((b) => b.dataset.filter === wanted) ? wanted : "all";
    archiveGrid?.classList.toggle("filtered", category !== "all");
    for (const item of filters) {
      const active = item.dataset.filter === category;
      item.classList.toggle("active", active);
      item.setAttribute("aria-pressed", String(active));
    }
    for (const post of posts) {
      const matches = (post.dataset.categories ?? "").split("|").includes(category);
      if (post === featuredSection) post.hidden = category !== "all";
      else if (post === featuredArchiveCard) post.hidden = category === "all" || !matches;
      else post.hidden = category !== "all" && !matches;
    }
    markTopRow();
    if (updateURL) {
      const url = new URL(location.href);
      if (category === "all") url.searchParams.delete("category");
      else url.searchParams.set("category", category);
      history.replaceState(null, "", url);
    }
  };
  const fromUrl = () => new URLSearchParams(location.search).get("category") ?? "all";
  for (const button of filters) {
    button.addEventListener("click", () => applyFilter(button.dataset.filter ?? "all", true));
  }
  applyFilter(fromUrl());
  addEventListener("popstate", () => applyFilter(fromUrl()));
  addEventListener("resize", markTopRow, { passive: true });
}

/** The article page's cover: a slow vertical shift with the scroll (none on phones / reduced motion). */
export function blogArticle(): void {
  const frame = document.querySelector<HTMLElement>(".article-hero-frame");
  const artwork = frame?.querySelector("img");
  if (!frame || !artwork) return;
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  const narrow = matchMedia("(max-width: 700px)");
  let raf = 0;
  const update = () => {
    raf = 0;
    if (reduced.matches || narrow.matches) {
      artwork.style.removeProperty("--article-art-shift");
      return;
    }
    const rect = frame.getBoundingClientRect();
    const progress = Math.max(
      0,
      Math.min(1, (innerHeight - rect.top) / (innerHeight + rect.height)),
    );
    const shift = (0.5 - progress) * 112;
    artwork.style.setProperty("--article-art-shift", `${shift.toFixed(2)}px`);
  };
  const queue = () => {
    if (!raf) raf = requestAnimationFrame(update);
  };
  addEventListener("scroll", queue, { passive: true });
  addEventListener("resize", queue, { passive: true });
  reduced.addEventListener("change", queue);
  narrow.addEventListener("change", queue);
  update();
}
